import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import {ColumnProps} from 'antd/lib/table';
import {Button, Switch, Table} from 'antd';

import locales from '../locales';
import {ErrorAlert} from '../common/ErrorAlert';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import furoApi from '../api/FuroApi';
import {
  Client,
  ClientInvoicesRequest,
  ClientSummary,
  ErrorResponse,
  Invoice,
  PageResponseEnvelope
} from '../api/dtos/dtos';
import * as tableUtils from '../utils/table-utils';
import {
  filterableColumn,
  GridState,
  searchableSortableColumn,
  simpleColumn,
  sortableColumn
} from '../utils/table-utils';
import {groupNumber, withCurrency} from '../utils/number-utils';
import * as paths from '../paths';
import {IdPageUrlParams} from '../paths';
import * as routerUtils from '../utils/router-utils';
import {YearMonth, YearMonthRangePicker} from "../statistics/utils/YearMonthRangePicker";
import * as moment from "moment/moment";
import {setAppHeaderSubtitle} from "../app/AppHeader";
import ClientDetailsAlert from "../client-details/ClientDetailsAlert";
import * as dateUtils from "../utils/date-utils";
import {Link} from "react-router-dom";
import {
  getInvoiceSourceFilters,
  getInvoiceStateFilters,
  InvoiceSource,
  renderInvoiceSource,
  renderInvoiceState
} from "./invoice";
import * as formUtils from "../utils/form-utils";
import FormItem from "antd/lib/form/FormItem";
import ButtonBar from "../common/ButtonBar";
import InvoiceHistoryPopover from "./InvoiceHistoryPopover";

interface LocationState {
  start?: YearMonth;
  end?: YearMonth;
  showDeletedOnly?: boolean;
}

interface State extends GridState<Invoice> {
  client?: ClientSummary;
  error?: ErrorResponse;
  start?: YearMonth;
  end?: YearMonth;
  showDeletedOnly: boolean;
}

class ClientInvoicesPage extends React.Component<RouteComponentProps<IdPageUrlParams, {}, LocationState>, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RouteComponentProps<IdPageUrlParams, {}, LocationState>) {
    super(props);

    const {start, end, showDeletedOnly} = props.location.state || {};

    this.state = {
      // Default initial grid state
      ...tableUtils.initialGridState(),

      // Initial sorting
      sortColumnKey: 'createdAt',
      sortOrder: 'descend',
      showDeletedOnly: showDeletedOnly ?? false,

      start: start ? start : {
        year: moment().get('year'),
        month: moment().get('month')
      },
      end: end ? end : {
        year: moment().get('year'),
        month: moment().get('month')
      },
    };
  }

  public componentDidMount() {
    this.loadClient();
    this.reload();
  }

  public render() {
    const {error, start, end, client, showDeletedOnly} = this.state;
    const columns: Array<ColumnProps<Invoice>> = [
      simpleColumn(locales.type, r => <InvoiceHistoryPopover key={r.id} invoice={r}/>),
      searchableSortableColumn(locales.invoice_number, 'invoiceNumber', this.state, this.renderInvoiceNumber),
      sortableColumn(locales.created_at, 'createdAt', this.state, this.renderCreatedAt),
      sortableColumn(locales.last_changed_at, 'lastChangedAt', this.state, this.renderLastChangedAt),
      searchableSortableColumn(locales.last_changed_by, 'lastChangedBy', this.state, this.renderLastChangedBy),
      filterableColumn(locales.state, 'state', this.state, this.renderState, getInvoiceStateFilters(), false),
      filterableColumn(locales.source, 'source', this.state, renderInvoiceSource, getInvoiceSourceFilters(), true),
      simpleColumn(locales.entries, this.renderEntryCount),
      sortableColumn(locales.total, 'total', this.state, this.renderTotal),
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <YearMonthRangePicker
          start={start}
          end={end}
          onRangeClear={this.onRangeClear}
          onRangePick={this.onRangePick}
        />

        {client &&
            <FormItem {...formUtils.formItemLayout} label={locales.client}>
                <ClientDetailsAlert
                    closable={false}
                    client={client}
                />
            </FormItem>
        }

        <ButtonBar style={{marginBottom: 8}}>
          <span>{locales.show_only_deleted}</span>
          <Switch
            style={{marginRight: 16, marginLeft: 4}}
            checked={showDeletedOnly}
            onChange={this.onShowDeletedOnlyChange}
          />
          <Button
            size="small"
            type="primary"
            icon="plus"
            onClick={this.onAddInvoiceClick}
            disabled={!client}
          >
            {locales.add}
          </Button>
        </ButtonBar>

        {start && end && (

          <Table
            dataSource={this.state.data}
            columns={columns}
            rowKey={tableUtils.idRowKeyFn}
            bordered={true}
            size="small"
            pagination={tableUtils.defaultPaginationConfig(this.state)}
            onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
            scroll={{x: 1200}}
          />

        )}

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private renderInvoiceNumber = (record: Invoice): React.ReactNode => {
    return <Link to={paths.viewInvoicePath(record.id)}>{record.externalNumber ?? record.internalNumber}</Link>
  };

  private renderCreatedAt = (record: Invoice): React.ReactNode => {
    return dateUtils.formatDateTimeOrNull(record.createdAt) || '-';
  };

  private renderLastChangedAt = (record: Invoice): React.ReactNode => {
    return dateUtils.formatDateTimeOrNull(record.lastChangedAt) || '-';
  };

  private renderLastChangedBy = (record: Invoice): React.ReactNode => {
    return record.lastChangedBy || '-';
  };

  private renderState = (record: Invoice): React.ReactNode => {
    return renderInvoiceState(record.state);
  };

  private renderEntryCount = (record: Invoice): React.ReactNode => {
    const entriesLength = record.entries?.length;
    return record.entries ? groupNumber(entriesLength) : '-';
  };

  private renderTotal = (record: Invoice): React.ReactNode => {
    return record.total === undefined ? '-' : withCurrency(groupNumber(record.total));
  };

  private onGridChange = (newState: GridState<Invoice>) => {
    this.setState({...newState}, () => this.reload());
  };

  private onShowDeletedOnlyChange = (checked: boolean) => {
    this.setState({showDeletedOnly: checked, page: 1}, () => this.reload());
  };

  private onAddInvoiceClick = () => {
    this.props.history.push(paths.createInvoicePath, {
      client: this.state.client,
      year: this.state.start?.year,
      month: this.state.start?.month,
      source: InvoiceSource.MANUAL,
    });
  };

  private onRangeClear = () => {
    this.setState({
      start: undefined,
      end: undefined,
    });
  };

  private onRangePick = (start: YearMonth | undefined, end: YearMonth | undefined) => {
    this.setState({start, end}, () => {
      if (start && end) {
        this.reload();
      }
    });
  };

  private getClientIdFromUrl = (): number => {
    return Number(this.props.match.params.id);
  };

  private loadClient = () => {
    const clientId = this.getClientIdFromUrl();
    const promise = furoApi.clients.get(clientId)
      .then(this.onLoadClientSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadClientSuccess = (response: Client) => {
    this.setState({client: response}, () =>
      setAppHeaderSubtitle(this.props, response.name)
    );
  };


  private reload = () => {
    const {start, end} = this.state;
    const clientId = this.getClientIdFromUrl();

    const request: ClientInvoicesRequest = {
      grid: tableUtils.toGridRequest(this.state),
      clientId,
      deleted: this.state.showDeletedOnly,
      startYear: start?.year!,
      startMonth: start?.month!,
      endYear: end?.year!,
      endMonth: end?.month!,
    };

    const promise = furoApi.invoicing.getClientInvoices(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: PageResponseEnvelope<Invoice>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
      setAppHeaderSubtitle(this.props, this.state.client?.name || '');
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };
}

export default withRouter(ClientInvoicesPage);



// WEBPACK FOOTER //
// ./src/invoicing/ClientInvoicesPage.tsx